import { RefObject, useEffect, useRef, useState } from 'react'

import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'
import { generateRandomString } from 'aem-libs-ts/src/utils/string/generateRandomString'

import { SelectValueType } from '../types'


const useFocusListItem = <TValue extends SelectValueType>(
  itemsVisibility: boolean,
  value: TValue,
  focusedValue: TValue | null,
  skipFocus: boolean
) => {
  const listRef: RefObject<HTMLDivElement | null> = useRef(null)
  const itemBaseId: RefObject<string> = useRef(generateRandomString())

  const getElementByValue = (value: TValue) => {
    return document.querySelector(
      "[data-select-item='" +
        itemBaseId.current +
        '__' +
        (typeof value === 'string' ? value.replace(/'/, '%27') : value) +
        "']"
    ) as HTMLElement // eslint-disable-line
  }

  useEffect(() => {
    if (!itemsVisibility || !isDefined(value) || skipFocus) {
      return
    }

    const itemEl = getElementByValue(value) // eslint-disable-line

    if (itemEl && listRef.current) {
      const offset = itemEl.getBoundingClientRect().top - listRef.current.getBoundingClientRect().top
      listRef.current.scrollTop = isNaN(offset) ? 0 : offset
      itemEl.focus()
    }
  }, [itemsVisibility])

  useEffect(() => {
    if (!itemsVisibility || !focusedValue) {
      return
    }

    const itemEl = getElementByValue(focusedValue)
    if (itemEl) {
      itemEl.focus()
    }
  }, [itemsVisibility, focusedValue])

  return { listRef, itemBaseId }
}

export const useSelectBox = <TOption, TValue extends SelectValueType>(
  renderedItems: TOption[],
  value: TValue,
  options: { disabled?: boolean; searchEnabled?: boolean }
) => {
  const [isOpen, setIsOpen] = useState(false)
  const [focusedValue, setFocusedValue] = useState<TValue | null>(null)
  const { listRef, itemBaseId } = useFocusListItem(isOpen, value, focusedValue, options.searchEnabled ?? false)

  const switchOpen = () => {
    if (options.disabled) {
      return
    }
    setIsOpen((v) => !v)
  }

  const closeItems = () => setIsOpen(false)
  const openItems = () => setIsOpen(true)

  return { listRef, itemBaseId, isOpen, switchOpen, closeItems, openItems, setFocusedValue, focusedValue }
}
